import {
  Box,
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import BulkUpdatePopup from "../../Pages/FundAllocator/Popup/FundAllocator";
import BuildIcon from "@mui/icons-material/Build";

const AllocatorList = ({
  order,
  orderBy,
  headLabel,
  onRequestSort,
  fundId,
  matchedAllocations,
  editableValue,
  editableData,
  setEditableData,
  custodians,
  sortedData,
  setStrategyCombined,
  setStrategyLowAum,
  setHasChanges,
  selectedRows,
  handleSelectAll,
  handleBulkBrokerChange,
  handleBulkAccountChange,
  strategies,
  StrategiesCombined,
  preserveEditableDataRef,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [, setSelectedColumn] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleBulk = (event) => {
    setOpenPopup(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedColumn(null);
  };

  // Filter out strategies already in the table, then sort A–Z ignoring case
  const availableStrategies = (strategies
    ?.filter(
      (strategy) =>
        !StrategiesCombined?.some(
          (combined) => combined.strategyId === strategy.strategyId
        )
    ) || [])
    .slice()
    .sort((a, b) =>
      (a?.name || "")
        .toLowerCase()
        .localeCompare((b?.name || "").toLowerCase())
    );

  const handleAddStrategy = (item) => {
    handleClose();
    
    // Set the ref to true to preserve editableData in the useEffect
    if (preserveEditableDataRef) {
      preserveEditableDataRef.current = true;
    }
    
    setHasChanges(true);
    setStrategyCombined((prev) => [...prev, item]);
    setStrategyLowAum((prev) => prev.filter((i) => i !== item));
  };

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  const isAddStrategyDisabled =
    !availableStrategies || availableStrategies.length === 0;
  const isBulkUpdate =
    fundId != "" &&
    fundId !== undefined &&
    fundId !== null &&
    matchedAllocations &&
    matchedAllocations.length;

  return (
    <>
      <TableHead>
        <TableRow
          sx={{
            "&:hover": {
              background: theme.palette.grey[200],
              cursor: "pointer",
            },
          }}
        >
          <TableCell
            align="left"
            colSpan={1}
            sx={{
              fontSize: theme.typography.bodyText12.fontSize,
              fontWeight: 900,
              background: "white",
              padding: 0,
              paddingLeft: "20px",
              textAlign: "left",
              cursor: isAddStrategyDisabled ? "not-allowed" : "pointer",
              "&:hover": {
                backgroundColor: isAddStrategyDisabled ? "white" : "lightgrey",
              },
            }}
            onClick={isAddStrategyDisabled ? null : handleClick}
          >
            <Box 
              display="flex" 
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography
                sx={{
                  fontSize: theme.typography.bodyText12.fontSize,
                  fontWeight: 900,
                  color: "#000000",
                }}
              >
                Add Strategy
              </Typography>
              <IconButton
                aria-label="add"
                sx={{
                  height: "16px",
                  width: "16px",
                  mr:1,
                }}
                disabled={isAddStrategyDisabled}
              >
                <AddIcon
                  sx={{
                    height: "20px",
                    width: "20px",
                    color: "black",
                  }}
                />
              </IconButton>
            </Box>
          </TableCell>

          <TableCell
            align="center"
            colSpan={4}
            sx={{
              fontSize: theme.typography.bodyText12.fontSize,
              fontWeight: 900,
              backgroundColor: "#2C3E50",
              padding: 0,
              paddingLeft: "20px",
              textAlign: "center",
              color: "#FFFFFF",
            }}
          >
            Existing
          </TableCell>
          <TableCell
            align="center"
            colSpan={5}
            sx={{
              fontSize: theme.typography.bodyText12.fontSize,
              fontWeight: 900,
              backgroundColor: "#34495E",
              padding: 0,
              paddingLeft: "20px",
              textAlign: "center",
              color: "#FFFFFF",
            }}
          >
            Target
          </TableCell>
          <TableCell
            align="center"
            colSpan={3}
            sx={{
              fontSize: theme.typography.bodyText12.fontSize,
              fontWeight: 900,
              backgroundColor: "#5D6D7E",
              padding: 0,
              paddingLeft: "20px",
              "&:hover": {
                backgroundColor: "#5D6D7E",
              },
              color: "#FFFFFF",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                sx={{
                  fontSize: theme.typography.bodyText12.fontSize,
                  fontWeight: '900 !important',
                  marginRight: "8px",
                }}
              >
                Preferences{" "}
              </Typography>

              {isBulkUpdate && selectedRows.length > 0 && (
                <IconButton
                  aria-label="add"
                  sx={{
                    height: "16px",
                    width: "16px",
                    padding: 0,
                  }}
                  onClick={handleBulk}
                >
                  <BuildIcon
                    sx={{
                      height: "20px",
                      width: "20px",
                      color: "black",
                    }}
                  />
                </IconButton>
              )}
            </Box>
          </TableCell>
        </TableRow>

        <TableRow sx={{ background: theme.palette.grey[700] }}>
          {headLabel?.map((headCell, index) => (
            <TableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              sx={{
                color: theme.palette.lightGrey.textColor,
                background: "#d9dcdf",
                fontSize: "11px",
                width: headCell.id === "ne" ? "10px" : "",
                padding: "2px",
                borderLeft: "0.1px solid #ccc3c3",
                borderRight: "0.1px solid #ccc3c3",
                alignItems: "center",
                "&:hover": {
                  background: theme.palette.lightGrey.tableHeader,
                  cursor: "pointer",
                },
              }}
            >
              {index === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: "8px",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selectedRows.length > 0 && selectedRows.length === editableData?.length}
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < editableData?.length
                    }
                    onChange={handleSelectAll}
                    sx={{
                      "& .MuiSvgIcon-root": { fontSize: 16 },
                      padding: "0px",
                      ml: "5px"
                    }}
                  />
                  <TableSortLabel
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={createSortHandler(headCell.id)}
                  >
                    <Typography
                      sx={{
                        color: "black !important",
                        fontSize: "12px",
                        textAlign: "center",
                      }}
                    >
                      {headCell.label}
                    </Typography>
                  </TableSortLabel>
                </Box>
              ) : (
                <TableSortLabel
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  <Typography
                    sx={{
                      color: "black !important",
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {headCell.label}
                  </Typography>
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          {availableStrategies?.map((strategy) => (
            <MenuItem
              key={strategy?.strategyId}
              onClick={() => handleAddStrategy(strategy)}
            >
              {strategy?.name}
            </MenuItem>
          ))}
        </Menu>
      </TableHead>
      <BulkUpdatePopup
        sortedData={sortedData}
        custodians={custodians}
        matchedAllocations={matchedAllocations}
        setEditableData={setEditableData}
        editableData={editableData}
        fundId={fundId}
        editableValue={editableValue}
        setHasChanges={setHasChanges}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        title="Bulk Update"
        selectedRows={selectedRows}
        handleBulkBrokerChange={handleBulkBrokerChange}
        handleBulkAccountChange={handleBulkAccountChange}
      />
    </>
  );
};

export default AllocatorList;
